import get from "lodash/get";
import includes from "lodash/includes";
import { action, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import swal from "sweetalert2";
import { getPatientSummary } from "../../serviceCalls";
import Loader from "../../shared/Loader/Loader";
import Widget from "../Widget";
import "./PatientTiles.scss";
import PatientTilesStore from "./PatientTilesStore";
import {
  AllergiesTile,
  CareTeamTile,
  DemographicsTile,
  DocumentsTile,
  DxProblemsTile,
  ECGTile,
  ImagesTile,
  LabsTile,
  MedsTile,
  MonitorTile,
  VentilatorTile,
  VitalsTile,
} from "./tiles";
import patientNavigationPath from "../../shared/patientNavigationPath";

const tileViews = {
  demographics: DemographicsTile,
  allergies: AllergiesTile,
  careteam: CareTeamTile,
  dxproblems: DxProblemsTile,
  labs: LabsTile,
  vitals: VitalsTile,
  meds: MedsTile,
  monitor: MonitorTile,
  documents: DocumentsTile,
  ventilator: VentilatorTile,
  ecgs: ECGTile,
  images: ImagesTile,
};

let tileSequence = [
  "demographics",
  "allergies",
  "careteam",
  "dxproblems",
  "vitals",
  "obvitals",
  "meds",
  "labs",
  "ecgs",
  "monitor",
  "documents",
  "documents_outpatient",
  "images",
  "ventilator",
  "cervical_exams",
  "ob_annotations",
  "ob_pitocin",
  "ob_other",
];

const tileTitles = {
  demographics: "General Information",
  careteam: "Care Team",
  dxproblems: "DX/Problems",
  ecgs: "ECGs",
  documents_outpatient: "Documents - Outpatient",
  meds: "Active Meds",
  images: "Imaging",
  documents: "documents",
};

class PatientTiles extends Widget {
  constructor(props) {
    super(props);

    makeObservable(this, {
      toggleOverflow: action,
      expandTile: action,
      getPatientSummary: action,
    });

    props.store.match = props.match;
    this.store = props.store.patientTilesStore = new PatientTilesStore();
    this.props.history.replace(`/patient/${this.asmpid}/overview`);
    if (this.props.store.patient) {
      this.asmpid = this.props.store.patient.asmpid;
    }

    if (props.match.params.tile) {
      tileSequence = [props.match.params.tile];
    }
    this.getPatientSummary();
    this.refresh = this.refresh.bind(this);
  }

  render() {
    return (
      <div className="tiles">
        {this.store.tiles ? (
          tileSequence.map((key, i) => {
            const tileData = this.store.tiles[key];
            const expanded = get(tileData, "overflow.expanded");

            return tileData ? (
              <div
                className={`tile ${key} ${this.cn({
                  expanded,
                  "has-overflow": tileData.overflow,
                })}`}
                key={i}
                onClick={(e) => this.expandTile({ tileData, key }, e)}
                ref={(ref) => (this[`ref-${key}`] = ref)}
                style={{
                  height: expanded
                    ? this[`ref-${key}`].style.height ||
                      this[`ref-${key}`].scrollHeight + 30
                    : undefined,
                }}
              >
                {tileViews[key] ? (
                  tileViews[key]({
                    model: tileData.model,
                    key,
                    prefs: this.props.store.prefs,
                    title: tileTitles[key] || key,
                    formatDate: this.date,
                  })
                ) : (
                  <pre>{JSON.stringify(tileData, null, 2)}</pre>
                )}
                {tileData.overflow ? (
                  <div
                    className="overflow"
                    onClick={(e) => this.toggleOverflow(e, key)}
                  ></div>
                ) : null}
              </div>
            ) : null;
          })
        ) : (
          <Loader />
        )}
      </div>
    );
  }
  componentDidUpdate() {
    if (this.refEcgs) {
      const ecgData = this.store.tiles.ecgs.model;
      const isOverflowing =
        this.refEcgs.scrollHeight > this.refEcgs.offsetHeight;
      this.refEcgs.querySelector(".count").style.display =
        ecgData.count > get(ecgData.testlist, "length", 0) || isOverflowing
          ? "block"
          : "none";
      if (isOverflowing && !ecgData.count) {
        ecgData.count = get(ecgData.testlist, "length", 0);
      }
    }
    if (this.store.tiles) {
      Object.keys(this.store.tiles).forEach((key) => {
        const tile = this[`ref-${key}`];
        if (!tile) return;
        if (
          tile.scrollHeight > tile.clientHeight &&
          !this.store.tiles[key].overflow
        ) {
          this.store.tiles[key] = {
            ...this.store.tiles[key],
            overflow: {},
          };
        }
      });
    }
  }

  refresh() {
    this.getPatientSummary();
  }

  toggleOverflow(e, key) {
    e.stopPropagation();
    requestAnimationFrame(() => this.render());
    this.store.tiles[key].overflow.expanded =
      !this.store.tiles[key].overflow.expanded;
    this.store.tiles[key] = { ...this.store.tiles[key] };
    requestAnimationFrame(
      () => (this.store.tiles[key] = { ...this.store.tiles[key] }),
    );
  }

  expandTile({ tileData, key }) {
    const tilesToExpand = [
      "demographics",
      "allergies",
      "careteam",
      "dxproblems",
    ];
    if (includes(tilesToExpand, key)) {
      swal.fire({
        customClass: `tile ${key}`,
        html: this[`ref-${key}`].innerHTML,
      });
    } else {
      this.props.history.push(
        patientNavigationPath(this.asmpid, tileData.model.status, key),
      );
    }
  }

  async getPatientSummary() {
    const res = await getPatientSummary({ asmpid: this.asmpid });
    const mrnField = res.tiles.demographics?.model.fields.find(
      (f) => f.label === "MRN",
    );
    if (mrnField)
      mrnField.label =
        this.props.store.prefs?.userPreferences?.server?.pm?.mrnLabel;
    this.store.tiles = res.tiles;
  }
}

export default inject("store")(observer(PatientTiles));
